import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/**
 * Vehicle detail shapes (M11). The M1 slim-down removed every column describing the car
 * along with the stock-management ones (ADR 0014), so an auction lot showed a plate and a
 * price; these restore the auction-relevant half of the legacy model. Money is integer
 * minor units (kuruş); timestamps are epoch-ms integers. See ADR 0025,
 * `docs/03-data-model.md` §vehicles and `docs/04-api-contract.md` § Vehicle management.
 *
 * Enum members are the legacy identifiers verbatim (the legacy locale file keyed its
 * labels on exactly these strings), so ported data and the ingestion API line up without a
 * translation layer. Labels live in `@bia-auction/shared-i18n`.
 */

// --- Enumerations -----------------------------------------------------------

export const VEHICLE_TYPES = [
  'automobile',
  'suv',
  'commercial',
  'heavy-commercial',
  'motorcycles',
  'minivans',
  'bus',
  'truck',
  'damaged',
  'earth-movers',
] as const;
export const vehicleTypeSchema = z.enum(VEHICLE_TYPES);
export type VehicleType = z.infer<typeof vehicleTypeSchema>;

export const FUELS = ['gasoline', 'gasoline-lpg', 'diesel', 'lpg', 'hybrid', 'electric'] as const;
export const fuelSchema = z.enum(FUELS);
export type Fuel = z.infer<typeof fuelSchema>;

export const GEARS = [
  'automatic',
  'semi-automatic',
  'tiptronic',
  'manual',
  'cvt',
  's-tronic',
] as const;
export const gearSchema = z.enum(GEARS);
export type Gear = z.infer<typeof gearSchema>;

export const BODY_TYPES = [
  'Cabrio',
  'Coupe',
  'Hatchback 3-door',
  'Hatchback 5-door',
  'Sedan',
  'Station Wagon',
  'Crossover',
  'MPV',
  'Roadster',
  'Off-road',
] as const;
export const bodyTypeSchema = z.enum(BODY_TYPES);
export type BodyType = z.infer<typeof bodyTypeSchema>;

export const VEHICLE_CONDITIONS = ['new', 'used', 'free-certified'] as const;
export const vehicleConditionSchema = z.enum(VEHICLE_CONDITIONS);
export type VehicleCondition = z.infer<typeof vehicleConditionSchema>;

/** The 14 body parts of the paint/replacement report, in the legacy walk-around order. */
export const EXPERTISE_PARTS = [
  'frontBumper',
  'frontHood',
  'roof',
  'frontRightMudguard',
  'frontRightDoor',
  'rearRightDoor',
  'rearRightMudguard',
  'frontLeftMudguard',
  'frontLeftDoor',
  'rearLeftDoor',
  'rearLeftMudguard',
  'rearHood',
  'rearBumper',
  'spareTire',
] as const;
export type ExpertisePart = (typeof EXPERTISE_PARTS)[number];

export const EXPERTISE_STATES = [
  'original',
  'changed',
  'painted',
  'local-painted',
  'crushed',
  'sketched',
  'registered',
] as const;
export const expertiseStateSchema = z.enum(EXPERTISE_STATES);
export type ExpertiseState = z.infer<typeof expertiseStateSchema>;

/**
 * Paint/replacement report. Partial by design: an unassessed part is absent rather than
 * defaulted to `original` — claiming "original" for a part nobody looked at would be a
 * statement about the car we cannot back. `isOriginalVehicle` therefore requires a
 * *complete* report (see below).
 */
// `z.partialRecord`, NOT `z.record`: in Zod 4 a record keyed by an enum is EXHAUSTIVE — it
// would reject any report missing a part, which is exactly the half-filled report the admin
// saves while walking around the car.
export const expertiseReportSchema = z.partialRecord(
  z.enum(EXPERTISE_PARTS),
  expertiseStateSchema,
);
export type ExpertiseReport = z.infer<typeof expertiseReportSchema>;

/**
 * The legacy "Orijinal" badge (`car.isOriginal`), derived and never stored. Every one of the
 * 14 parts must be present AND `original` — a partially-filled report is not a clean one.
 */
export function isOriginalVehicle(report: ExpertiseReport | null | undefined): boolean {
  if (!report) return false;
  return EXPERTISE_PARTS.every((part) => report[part] === 'original');
}

export const EQUIPMENT_KINDS = [
  'internal',
  'external',
  'security',
  'multimedia',
  'other',
] as const;
export const equipmentKindSchema = z.enum(EQUIPMENT_KINDS);
export type EquipmentKind = z.infer<typeof equipmentKindSchema>;

/** Selected equipment, grouped by kind. Values are `equipment_definitions.id` slugs. */
export const equipmentSelectionSchema = z.object({
  internal: z.array(z.string()).default([]),
  external: z.array(z.string()).default([]),
  security: z.array(z.string()).default([]),
  multimedia: z.array(z.string()).default([]),
  other: z.array(z.string()).default([]),
});
export type EquipmentSelection = z.infer<typeof equipmentSelectionSchema>;

/** The legacy `has` block — what physically comes with the car at hand-over. */
export const CHECKLIST_ITEMS = [
  'warranty',
  'key',
  'spareKey',
  'license',
  'spareTyre',
  'lifter',
  'trafficSet',
  'chain',
  'floorMat',
  'winterTire',
  'jackLever',
  'extinguisher',
  'wheelScrew',
  'serviceManualsWarranty',
  'doublePlateSealControl',
  'firstAidKit',
  'extraTireSet',
] as const;
export type ChecklistItem = (typeof CHECKLIST_ITEMS)[number];

/**
 * Partial: an absent item is "not recorded", which is not the same claim as `false`.
 * `partialRecord` for the same reason as {@link expertiseReportSchema}.
 */
export const checklistSchema = z.partialRecord(z.enum(CHECKLIST_ITEMS), z.boolean());
export type Checklist = z.infer<typeof checklistSchema>;

// --- POST/PATCH /admin/vehicles ---------------------------------------------

/**
 * Vehicle create body. `plate` and `startingPrice` are the only required fields: a positive
 * starting price is the auction-eligibility gate (`vehicles.auction_price`), and everything
 * else can be filled in later from the edit form as the car is inspected.
 */
export const vehicleUpsertSchema = z.object({
  // Identity
  plate: z.string().min(1).max(32),
  oldPlate: z.string().max(32).nullable().optional(),
  chassis: z.string().max(64).nullable().optional(),
  motor: z.string().max(64).nullable().optional(),
  clientRef: z.string().max(120).nullable().optional(),
  vehicleType: vehicleTypeSchema.nullable().optional(),
  disabilityVehicle: z.boolean().optional(),
  // Display
  brand: z.string().max(120).nullable().optional(),
  series: z.string().max(120).nullable().optional(),
  modelName: z.string().max(200).nullable().optional(),
  year: z.number().int().min(1900).max(2200).nullable().optional(),
  color: z.string().max(60).nullable().optional(),
  km: z.number().int().min(0).nullable().optional(),
  condition: vehicleConditionSchema.nullable().optional(),
  fuel: fuelSchema.nullable().optional(),
  gear: gearSchema.nullable().optional(),
  bodyType: bodyTypeSchema.nullable().optional(),
  engineCapacity: z.number().int().min(0).nullable().optional(), // cc
  enginePower: z.number().int().min(0).nullable().optional(), // hp
  // Condition / paperwork
  pledgeBank: z.string().max(120).nullable().optional(),
  fuelStatus: z.number().int().min(0).max(100).nullable().optional(),
  insuranceValidUntil: z.number().int().nullable().optional(), // epoch-ms
  inspectionValidUntil: z.number().int().nullable().optional(), // epoch-ms
  // Content
  description: z.string().max(5000).nullable().optional(),
  equipments: equipmentSelectionSchema.nullable().optional(),
  checklist: checklistSchema.nullable().optional(),
  expertise: expertiseReportSchema.nullable().optional(),
  properties: z.record(z.string(), z.unknown()).nullable().optional(), // ingestion extras
  // Pricing (kuruş)
  startingPrice: z.number().int().positive(),
  reserve: z.number().int().positive().nullable().optional(),
  increment: z.number().int().positive().optional(),
  currency: z.string().max(8).optional(),
  relatedCompanyId: z.uuid().nullable().optional(),
});
export type VehicleUpsertInput = z.infer<typeof vehicleUpsertSchema>;

/**
 * Partial update. `.partial()` over the create shape, with at least one field required so a
 * `{}` body isn't a silent no-op that still writes an audit row.
 *
 * The price fields stay in the shape but the handler **refuses them while the vehicle is on
 * a `SCHEDULED`/`OPEN` lot** (409): a lot snapshots its price at creation, and repricing a
 * vehicle mid-auction would desync the lot the bidders are looking at.
 */
export const vehiclePatchAdminSchema = vehicleUpsertSchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type VehiclePatchAdminInput = z.infer<typeof vehiclePatchAdminSchema>;

/** The price fields frozen while the vehicle sits on an active lot. */
export const VEHICLE_PRICE_FIELDS = [
  'startingPrice',
  'reserve',
  'increment',
  'currency',
] as const satisfies readonly (keyof VehicleUpsertInput)[];

// --- Media ------------------------------------------------------------------

/** One table, four kinds — the legacy model's four parallel media arrays. */
export const MEDIA_KINDS = ['photo', 'video', 'damage_photo', 'damage_video'] as const;
export const mediaKindSchema = z.enum(MEDIA_KINDS);
export type MediaKind = z.infer<typeof mediaKindSchema>;

/**
 * What the browser/API client may send us. Deliberately narrow: an `<img>`/`<video>` source
 * we cannot re-encode server-side (ADR 0025 — no image processing in the Worker), so a
 * format the visitor's browser can't decode would simply be a broken gallery.
 */
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
] as const;
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'] as const;

/** Byte caps. Photos are browser-downscaled before upload, so 10 MB is already generous. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
/** How many URLs one ingestion submit may ask us to mirror. */
export const MAX_MIRROR_URLS = 24;

/** A gallery entry as projected to any client. The bytes are fetched from `url`. */
export const vehicleMediaSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  kind: mediaKindSchema,
  /** Ready-to-use path for `<img src>` — `/api/v2/media/vehicles/:vehicleId/:mediaId`. */
  url: z.string(),
  contentType: z.string(),
  bytes: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  sortIndex: z.number().int(),
  isCover: z.boolean(),
  showPublic: z.boolean(),
  tags: z.array(z.string()),
  createdAt: z.number().int(), // epoch-ms
});
export type VehicleMedia = z.infer<typeof vehicleMediaSchema>;

/** `PATCH …/media/:mediaId` — ordering and visibility only; bytes are immutable. */
export const vehicleMediaPatchSchema = z
  .object({
    kind: mediaKindSchema.optional(),
    sortIndex: z.number().int().min(0).optional(),
    isCover: z.boolean().optional(),
    showPublic: z.boolean().optional(),
    tags: z.array(z.string().max(60)).max(20).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type VehicleMediaPatchInput = z.infer<typeof vehicleMediaPatchSchema>;

/**
 * Outcome of mirroring one `mediaUrls[]` entry, surfaced on the submission rather than
 * failing the submit: a vehicle without its photos is still a valid vehicle, and a client
 * whose CDN 404s should not have its whole submission rejected.
 */
export const mediaMirrorResultSchema = z.object({
  url: z.string(),
  ok: z.boolean(),
  mediaId: z.uuid().optional(),
  error: z.string().optional(),
});
export type MediaMirrorResult = z.infer<typeof mediaMirrorResultSchema>;

// --- Read projections -------------------------------------------------------

/**
 * The narrow vehicle summary carried on every lot (M11) so a card can show what the car *is*
 * rather than just its plate. Deliberately small — the full record (description, equipments,
 * expertise, gallery) is one request away at `GET /auctions/:id/lots/:lotId`, and this shape
 * is repeated for every lot of a sale.
 */
export const lotVehicleSchema = z.object({
  brand: z.string().nullable(),
  series: z.string().nullable(),
  modelName: z.string().nullable(),
  year: z.number().int().nullable(),
  km: z.number().int().nullable(),
  color: z.string().nullable(),
  fuel: fuelSchema.nullable(),
  gear: gearSchema.nullable(),
  /** Ready-to-use path for the card thumbnail, or null when the gallery is empty. */
  coverUrl: z.string().nullable(),
});
export type LotVehicle = z.infer<typeof lotVehicleSchema>;

/**
 * The auction a vehicle is currently tied up in, named rather than implied. `committed` alone
 * answers "why can't I add this car to a sale" with a yes; this answers "to which one" — the
 * question an operator asks next, and the one the pool's per-auction filter is built around.
 *
 * **Scoped to what the caller could see in their own sale list**: an auction their firm scope
 * hides projects as `null`, the same rule the `?saleId=` filter applies, so the pool cannot
 * disclose through one what it withholds through the other. `committed` is unaffected — what
 * is withheld is *which* auction holds the car, never *that* one does.
 */
export const committedSaleSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string().nullable(),
});
export type CommittedSale = z.infer<typeof committedSaleSchema>;

/** Row shape of the admin vehicle list — enough to identify and price a car at a glance. */
export const vehicleSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  plate: z.string(),
  brand: z.string().nullable(),
  series: z.string().nullable(),
  modelName: z.string().nullable(),
  year: z.number().int().nullable(),
  km: z.number().int().nullable(),
  color: z.string().nullable(),
  fuel: fuelSchema.nullable(),
  gear: gearSchema.nullable(),
  startingPrice: z.number().int().nullable(), // kuruş
  currency: z.string(),
  relatedCompanyId: z.uuid().nullable(),
  /** The firm that submitted this vehicle through the ingestion API (M13); null if admin-entered. */
  submittedByCompany: z.uuid().nullable(),
  /** Ready-to-use path for the cover thumbnail, or null when the gallery is empty. */
  coverUrl: z.string().nullable(),
  /** Whether the vehicle is currently tied to a SCHEDULED/OPEN lot or already SOLD. */
  committed: z.boolean(),
  /**
   * Which auction ties it up — see {@link committedSaleSchema}. Null when the vehicle is free,
   * or when the committing auction is not one the caller could see in their own sale list.
   */
  committedSale: committedSaleSchema.nullable(),
});
export type VehicleSummary = z.infer<typeof vehicleSummarySchema>;

export const vehicleListSchema = paginatedSchema(vehicleSummarySchema);
export type VehicleList = z.infer<typeof vehicleListSchema>;

/**
 * Sentinel for the "unattached" half of an id filter: `saleId=none` is *no auction at all* and
 * `companyId=none` is *no firm assigned*. Both are questions an operator actually asks of the
 * pool ("what is free to list", "what still needs an owner") and neither is expressible by
 * omitting the parameter, which means "don't filter".
 */
export const VEHICLE_FILTER_NONE = 'none';

const filterIdSchema = z.union([z.literal(VEHICLE_FILTER_NONE), z.uuid()]);

/**
 * `GET /admin/vehicles` query contract. Every filter is optional and ANDed — with each other
 * and with the caller's firm scope, which they can only narrow, never widen (AGENTS.md
 * review-checklist item 4). Empty-string values are dropped by the route before parsing, so a
 * cleared `<select>` reads as "no filter" rather than a 400.
 */
export const vehicleListQuerySchema = paginationQuerySchema.extend({
  /** Free-text over code/plate/brand/series/modelName, Turkish-folded on both sides. */
  q: z.string().optional(),
  /** Vehicles with a lot in this auction — any pass, any lot state — or `none` for never-lotted. */
  saleId: filterIdSchema.optional(),
  /** Owning firm (`related_company_id`), or `none` for the unassigned. */
  companyId: filterIdSchema.optional(),
  /** The pool's one status: free to list vs tied to a SCHEDULED/OPEN lot or already SOLD. */
  status: z.enum(['available', 'committed']).optional(),
  /**
   * An inverted range (`yearMin` > `yearMax`) is **not** rejected here: it is unsatisfiable, so
   * it answers with an empty page, matching `auditLogQuerySchema`'s `from`/`to` and the rule
   * stated there — a filter combination a UI can produce mid-edit must not 400. "Backwards
   * range" versus "no such vehicles" is a distinction only the operator needs, so the admin
   * filter bar draws it (and withholds both bounds) rather than the contract.
   */
  yearMin: z.coerce.number().int().min(1900).max(2999).optional(),
  yearMax: z.coerce.number().int().min(1900).max(2999).optional(),
});
export type VehicleListQuery = z.infer<typeof vehicleListQuerySchema>;

/** Full admin projection: the summary plus everything the form edits. */
export const vehicleDetailSchema = vehicleSummarySchema.extend({
  oldPlate: z.string().nullable(),
  chassis: z.string().nullable(),
  motor: z.string().nullable(),
  clientRef: z.string().nullable(),
  vehicleType: vehicleTypeSchema.nullable(),
  disabilityVehicle: z.boolean(),
  condition: vehicleConditionSchema.nullable(),
  bodyType: bodyTypeSchema.nullable(),
  engineCapacity: z.number().int().nullable(),
  enginePower: z.number().int().nullable(),
  pledgeBank: z.string().nullable(),
  fuelStatus: z.number().int().nullable(),
  insuranceValidUntil: z.number().int().nullable(), // epoch-ms
  inspectionValidUntil: z.number().int().nullable(), // epoch-ms
  description: z.string().nullable(),
  equipments: equipmentSelectionSchema.nullable(),
  checklist: checklistSchema.nullable(),
  expertise: expertiseReportSchema.nullable(),
  /** Derived from a COMPLETE all-`original` report — never stored (legacy `isOriginal`). */
  isOriginal: z.boolean(),
  properties: z.record(z.string(), z.unknown()).nullable(),
  /** The hidden reserve default — manager-only, like the per-lot reserve (ADR 0013). */
  reserve: z.number().int().nullable().optional(),
  increment: z.number().int(), // kuruş
  /** The gallery, in `sortIndex` order. Empty until something is uploaded or mirrored. */
  media: z.array(vehicleMediaSchema).default([]),
});
export type VehicleDetail = z.infer<typeof vehicleDetailSchema>;

/**
 * What a **bidder** — or an anonymous visitor — may see about the car on the lot page.
 *
 * This is deliberately NOT `vehicleDetailSchema`. The lot endpoint first shipped returning the
 * admin projection wholesale, which handed every caller the operator's own columns: the
 * internal `code`, the client's `clientRef`, the owning `relatedCompanyId`, `submittedByCompany`,
 * the ingestion `properties` blob, and the vehicle's `startingPrice` — the last of which sat in
 * the payload beside a `priceMasked: true` lot whose price had been withheld (found on preview,
 * 2026-08-07; ADR 0025 correction).
 *
 * Money lives on the **lot**, which does its own masking — so it is absent here entirely rather
 * than masked twice. The identity fields stay in the shape but go `null` for a masked viewer:
 * a plate or a chassis number is a registry-lookup key, which is exactly why ADR 0025 keeps
 * them behind the login with the prices.
 */
export const lotVehicleDetailSchema = vehicleDetailSchema
  .omit({
    code: true,
    clientRef: true,
    relatedCompanyId: true,
    submittedByCompany: true,
    properties: true,
    reserve: true,
    increment: true,
    startingPrice: true,
    currency: true,
    committed: true,
    committedSale: true,
  })
  .extend({
    // `null` for an anonymous viewer — see above.
    plate: z.string().nullable(),
    oldPlate: z.string().nullable(),
    chassis: z.string().nullable(),
    motor: z.string().nullable(),
  });
export type LotVehicleDetail = z.infer<typeof lotVehicleDetailSchema>;

// --- Reference data ---------------------------------------------------------

export const carDefinitionSchema = z.object({
  id: z.string(),
  brand: z.string(),
  models: z.array(z.string()),
});
export type CarDefinition = z.infer<typeof carDefinitionSchema>;

export const equipmentDefinitionSchema = z.object({
  id: z.string(),
  nameTr: z.string(),
  nameEn: z.string(),
  kind: equipmentKindSchema,
});
export type EquipmentDefinition = z.infer<typeof equipmentDefinitionSchema>;
